// Procedural Web Audio — synth blips, no audio files. Extracted from walkit-portal.
//
// The hard-won bit is the iOS unlock: an installed (standalone) PWA on iOS mutes
// Web-Audio synth under the ring/silent switch until you play a *media element*, which
// flips the audio session to the "playback" category. So we start a tiny looping silent
// WAV inside the first user gesture, alongside resuming the AudioContext. This is the
// exact fix that made walkit's walk sound audible in the installed app — do not remove it.
//
// Tone-ready: games only ever call play('name') / defineSounds(...). The synth backend
// lives behind that surface, so it can be swapped for Tone.js later without touching callers.

export type Osc = OscillatorType; // 'sine' | 'square' | 'sawtooth' | 'triangle'

/** One scheduled tone within a sound. */
export interface ToneSpec {
  /** Start frequency (Hz). */
  freq: number;
  /** Optional glide target (Hz) — set for a pitch slide, omit for a steady note. */
  to?: number;
  /** Delay before this tone, in seconds (default 0). */
  start?: number;
  /** Duration in seconds (default 0.08). */
  dur?: number;
  /** Waveform (default 'sine'). */
  type?: Osc;
  /** Peak gain 0..1 (default 0.16). */
  vol?: number;
}

/** A named sound is one or more tones played together/sequenced. */
export type SoundSpec = ToneSpec[];

export interface AudioEngine {
  /** Is sound currently enabled? */
  readonly enabled: boolean;
  /** Toggle sound on/off (persist this yourself via save if desired). */
  setEnabled(on: boolean): void;
  /** Register named sounds; merges with any existing. */
  defineSounds(sounds: Record<string, SoundSpec>): void;
  /** Play a registered sound by name. No-op if disabled/unknown/locked. */
  play(name: string): void;
  /** Manually unlock (also wired to the first gesture automatically). */
  unlock(): void;
  /** True once the first gesture has unlocked audio. */
  readonly unlocked: boolean;
}

// A 0.25s silent WAV as a data URL — the media element whose playback flips the iOS
// audio session so synth is no longer gated by the silent switch.
function silentWavURL(): string {
  const sr = 8000;
  const n = Math.floor(sr * 0.25);
  const bytes = 44 + n * 2;
  const buf = new ArrayBuffer(bytes);
  const dv = new DataView(buf);
  const w = (o: number, s: string) => {
    for (let i = 0; i < s.length; i++) dv.setUint8(o + i, s.charCodeAt(i));
  };
  w(0, 'RIFF');
  dv.setUint32(4, bytes - 8, true);
  w(8, 'WAVE');
  w(12, 'fmt ');
  dv.setUint32(16, 16, true);
  dv.setUint16(20, 1, true);
  dv.setUint16(22, 1, true);
  dv.setUint32(24, sr, true);
  dv.setUint32(28, sr * 2, true);
  dv.setUint16(32, 2, true);
  dv.setUint16(34, 16, true);
  w(36, 'data');
  dv.setUint32(40, n * 2, true);
  let bin = '';
  const u8 = new Uint8Array(buf);
  for (let i = 0; i < u8.length; i++) bin += String.fromCharCode(u8[i]);
  return 'data:audio/wav;base64,' + btoa(bin);
}

export interface AudioOptions {
  /** Start enabled? Default true. */
  enabled?: boolean;
  /** Sounds to register up front. */
  sounds?: Record<string, SoundSpec>;
}

export function createAudio(opts: AudioOptions = {}): AudioEngine {
  let enabled = opts.enabled ?? true;
  let unlocked = false;
  let ctx: AudioContext | null = null;
  let silentEl: HTMLAudioElement | null = null;
  const registry: Record<string, SoundSpec> = { ...(opts.sounds ?? {}) };

  const AC = () => window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;

  const init = () => {
    if (ctx) return;
    try {
      ctx = new (AC())();
    } catch {
      ctx = null;
    }
  };
  const resume = () => {
    try {
      if (ctx && ctx.state !== 'running') void ctx.resume();
    } catch {
      /* ignore */
    }
  };
  const keepSession = () => {
    try {
      if (!silentEl) {
        const el = document.createElement('audio');
        el.src = silentWavURL();
        el.loop = true;
        el.setAttribute('playsinline', '');
        el.preload = 'auto';
        el.volume = 1;
        silentEl = el;
      }
      const p = silentEl.play();
      if (p && p.catch) p.catch(() => {});
    } catch {
      /* ignore */
    }
  };

  const unlock = () => {
    init();
    resume();
    keepSession();
    if (ctx) {
      try {
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        g.gain.setValueAtTime(0.0001, ctx.currentTime);
        o.connect(g).connect(ctx.destination);
        o.start();
        o.stop(ctx.currentTime + 0.02);
      } catch {
        /* ignore */
      }
    }
    unlocked = true;
  };

  const scheduleTone = (t: ToneSpec) => {
    if (!ctx) return;
    const now = ctx.currentTime + (t.start ?? 0);
    const dur = t.dur ?? 0.08;
    const vol = t.vol ?? 0.16;
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = t.type ?? 'sine';
    o.frequency.setValueAtTime(t.freq, now);
    if (t.to != null) o.frequency.exponentialRampToValueAtTime(Math.max(1, t.to), now + dur);
    g.gain.setValueAtTime(0.0001, now);
    g.gain.exponentialRampToValueAtTime(vol, now + 0.012);
    g.gain.exponentialRampToValueAtTime(0.0001, now + dur);
    o.connect(g).connect(ctx.destination);
    o.start(now);
    o.stop(now + dur + 0.03);
  };

  const play = (name: string) => {
    if (!enabled) return;
    init();
    resume();
    if (!ctx) return;
    const spec = registry[name];
    if (!spec) return;
    for (const tone of spec) scheduleTone(tone);
  };

  // Auto-unlock on the first gesture; re-arm the session when the app returns to foreground.
  const onceUnlock = () => {
    unlock();
    window.removeEventListener('pointerdown', onceUnlock, true);
  };
  window.addEventListener('pointerdown', onceUnlock, { once: true, capture: true });
  const rearm = () => {
    resume();
    if (unlocked) keepSession();
  };
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') rearm();
  });
  window.addEventListener('pageshow', rearm);
  window.addEventListener('focus', rearm);

  return {
    get enabled() {
      return enabled;
    },
    get unlocked() {
      return unlocked;
    },
    setEnabled(on: boolean) {
      enabled = on;
    },
    defineSounds(sounds: Record<string, SoundSpec>) {
      Object.assign(registry, sounds);
    },
    play,
    unlock,
  };
}
